import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Drawer,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerBody,
    DrawerFooter,
    Button,
    Spinner,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useGetCartQuery, useGetStorefrontQuery } from '../../Api/Api';
import CartItemsView from './CartItemsView';
import CartSummary from './CartSummary';
import { parseCartPageConfig } from '../Helpers/cartPageConfig';
import { elementStyleFrom } from '../Helpers/searchPageConfig';
import { useLocalization } from '../Helpers/useLocalization';
import { CART_DRAG_MIME, ICartItemIdentity, useAddToCartAction } from './useAddToCartAction';

interface ICartPanelProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

// A right-side slide-out cart. Reuses the exact same line-item rendering (CartItemsView) and
// action buttons (CartSummary) as the full Cart page, so it behaves and is styled identically —
// the only difference is that the summary/action buttons are pinned in the drawer footer.
// Shown only when the merchant enables cartPageConfig.openInPanel (wired in NavBar).
const CartPanel: React.FC<ICartPanelProps> = (props) => {
    const cart = useGetCartQuery();
    const navigate = useNavigate();
    const L = useLocalization();
    const storefrontSettings = useGetStorefrontQuery();
    const cartConfig = React.useMemo(() => parseCartPageConfig(storefrontSettings.data?.cartPageConfig), [storefrontSettings.data?.cartPageConfig]);
    const containerStyle = React.useMemo(() => elementStyleFrom(cartConfig.container), [cartConfig.container]);
    const titleStyle = React.useMemo(() => elementStyleFrom(cartConfig.title, 'heading'), [cartConfig.title]);
    const summaryBoxStyle = React.useMemo(() => elementStyleFrom(cartConfig.summaryBox), [cartConfig.summaryBox]);

    const isEmpty = (cart.data?.products?.length ?? 0) === 0 && (cart.data?.bundles?.length ?? 0) === 0;

    const close = () => props.onOpenChange(false);

    // Drag-and-drop: let a product/bundle card be dropped anywhere in the open panel to add it,
    // mirroring the header cart-icon drop target. Requires the drawer to be non-modal (below) so
    // the page behind stays interactive and the card can actually be dragged while the panel is open.
    const addToCartAction = useAddToCartAction();
    const [dropActive, setDropActive] = React.useState(false);
    // Count of drops still being added. The add is async and the cart query only refetches once the
    // server responds, so we show an "Adding…" placeholder for each in-flight drop — otherwise the
    // panel looks frozen between the drop and the new row appearing. A counter (not a boolean) keeps
    // the placeholder accurate when several cards are dropped in quick succession.
    const [pendingAdds, setPendingAdds] = React.useState(0);
    const dragHasCartItem = (e: React.DragEvent) => e.dataTransfer.types.includes(CART_DRAG_MIME);
    const onDragOver = (e: React.DragEvent) => {
        if (dragHasCartItem(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!dropActive) setDropActive(true); }
    };
    const onDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropActive(false);
    };
    const onDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setDropActive(false);
        const raw = e.dataTransfer.getData(CART_DRAG_MIME);
        if (!raw) return;
        let item: ICartItemIdentity;
        try { item = JSON.parse(raw); } catch { return; }
        setPendingAdds((n) => n + 1);
        try {
            await addToCartAction(item);
        } finally {
            setPendingAdds((n) => Math.max(0, n - 1));
        }
    };

    return (
        // Non-modal: no backdrop scrim, so the home/search page behind stays interactive and cards
        // can be dragged into the panel (a modal drawer's mask would block the drag entirely).
        <Drawer
            position='end'
            size='medium'
            modalType='non-modal'
            open={props.open}
            onOpenChange={(_, { open }) => props.onOpenChange(open)}
        >
            <DrawerHeader style={containerStyle}>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance='subtle'
                            aria-label='Close'
                            icon={<Dismiss24Regular />}
                            onClick={close}
                        />
                    }
                >
                    <span style={titleStyle}>{L('localization_cart_page_title')}</span>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody
                style={{
                    ...containerStyle,
                    outline: dropActive ? '2px dashed currentColor' : undefined,
                    outlineOffset: '-8px',
                    transition: 'outline-color 0.15s',
                }}
                title={dropActive ? 'Drop to add to cart' : undefined}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                {pendingAdds > 0 &&
                    <div
                        className='cart-panel-adding'
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 10, borderRadius: 8, border: '1px dashed currentColor' }}
                    >
                        <Spinner size='tiny' />
                        <span>{pendingAdds > 1 ? `${L('localization_cart_adding', 'Adding to cart…')} (${pendingAdds})` : L('localization_cart_adding', 'Adding to cart…')}</span>
                    </div>
                }
                <CartItemsView
                    layout='compact'
                    emptyIconSize='8em'
                    onEmptyAction={() => { close(); navigate('/'); }}
                />
            </DrawerBody>
            {!isEmpty &&
                <DrawerFooter style={{ ...containerStyle, display: 'block' }}>
                    <div className='cart-summary' style={{ ...summaryBoxStyle, width: '100%' }}>
                        <CartSummary cart={cart.data} />
                    </div>
                </DrawerFooter>
            }
        </Drawer>
    );
};

export default CartPanel;
